import * as THREE from 'three';
import { Granit } from 'src/services/granits';
import { getPieceSize } from 'src/utils/configurator.utils';
import { configureHDTexture } from 'src/utils/textureLoader';
import {
  getIndexType,
  getLocalIndex,
  getGlobalIndex,
  isTwoSteleEqual,
  getDimensionsWithGeometry,
} from 'src/utils/3d.utils';

export const checkMonumentSizeWithGranit = (
  pieceGeometry: Float32Array,
  granitMaxLength: string,
  setError: (error: string) => void,
) => {
  const size = {
    x: getPieceSize('x', pieceGeometry),
    y: getPieceSize('y', pieceGeometry),
    z: getPieceSize('z', pieceGeometry),
  };

  if (
    Number.parseFloat(size.x) > Number.parseFloat(granitMaxLength) ||
    Number.parseFloat(size.y) > Number.parseFloat(granitMaxLength) ||
    Number.parseFloat(size.z) > Number.parseFloat(granitMaxLength)
  ) {
    setError(
      `Attention, une de vos pièces dépasse la taille maximale de ce granit (${Number.parseFloat(
        granitMaxLength,
      )}). Elle sera coupée.`,
    );
  }
};

export const changeTexture = (
  granit: Granit,
  indexs: number[],
  buckets: { BUCKET_3D_PIECES?: string },
  activePieces: any[],
  customSteles: any[],
  customPlatings: any[],
  customSoles: any[],
  pieces: any[],
  hasAdvancedMode: boolean,
  setActivePieces: (pieces: any[]) => void,
  setCustomSteles: (steles: any[]) => void,
  setCustomPlatings: (platings: any[]) => void,
  setCustomSoles: (soles: any[]) => void,
  setError: (error: string | undefined) => void,
) => {
  const loader = new THREE.TextureLoader();
  const loadedTexture = loader.load(
    `${buckets?.BUCKET_3D_PIECES || ''}/${granit.image}?t=${Date.now()}`,
    (texture) => {
      configureHDTexture(texture);
    },
  );

  let newActivePieces = [...activePieces];
  let newCustomStele = [...customSteles];
  let newCustomPlating = [...customPlatings];
  let newCustomSoles = [...customSoles];

  for (const index of indexs) {
    let typeOfPiece = 'activePieces';

    typeOfPiece = getIndexType(index, activePieces, customSteles, customPlatings, customSoles);

    if (typeOfPiece === 'activePieces') {
      loadedTexture.wrapS = THREE.RepeatWrapping;
      loadedTexture.wrapT = THREE.MirroredRepeatWrapping;
      loadedTexture.repeat.x = 1;

      newActivePieces = newActivePieces.map((el, elIndex) => {
        if (
          elIndex === index ||
          (activePieces[index].underAssembly &&
            activePieces[index].underAssembly.uuid === activePieces[elIndex].underAssembly?.uuid &&
            !hasAdvancedMode)
        ) {
          if (granit.material !== 'INOX') {
            checkMonumentSizeWithGranit(
              pieces[index].geometry.attributes.position.array,
              String(granit.maxLength),
              setError,
            );
          }
          return {
            ...el,
            granit,
            texture: loadedTexture,
          };
        }
        return el;
      });
    } else if (typeOfPiece === 'customStele') {
      loadedTexture.wrapS = THREE.RepeatWrapping;
      loadedTexture.wrapT = THREE.MirroredRepeatWrapping;
      loadedTexture.offset.x = 0.5;
      loadedTexture.repeat.x = 0.5;
      loadedTexture.repeat.y = 0.5;
      newCustomStele = newCustomStele.map((el, elIndex) => {
        if (index === activePieces.length + elIndex) {
          const extrudeGeometry = new THREE.ExtrudeGeometry(el.shape, el.extrudeSettings);
          if (granit.material !== 'INOX') {
            checkMonumentSizeWithGranit(
              // @ts-ignore
              extrudeGeometry.attributes.position.array,
              String(granit.maxLength),
              setError,
            );
          }
          return {
            ...el,
            granit,
            texture: loadedTexture,
          };
        }
        return el;
      });
    } else if (typeOfPiece === 'customPlating') {
      loadedTexture.wrapS = THREE.RepeatWrapping;
      loadedTexture.wrapT = THREE.MirroredRepeatWrapping;
      loadedTexture.offset.x = 0.5;
      loadedTexture.repeat.x = 0.5;
      loadedTexture.repeat.y = 0.5;
      newCustomPlating = newCustomPlating.map((el, elIndex) => {
        const deltaIndex = (activePieces.length ?? 0) + (customSteles.length ?? 0);
        if (index >= deltaIndex && index < deltaIndex + (customPlatings.length ?? 0)) {
          if (hasAdvancedMode && index - deltaIndex !== elIndex) return el;
          const extrudeGeometry = new THREE.ExtrudeGeometry(el.shape, {
            curveSegments: 12,
            steps: 2,
            depth: el.shapeDetails.depth,
            bevelEnabled: false,
          });
          if (granit.material !== 'INOX') {
            checkMonumentSizeWithGranit(
              // @ts-ignore
              extrudeGeometry.attributes.position.array,
              String(granit.maxLength),
              setError,
            );
          }

          return {
            ...el,
            granit,
            texture: loadedTexture,
          };
        }
        return el;
      });
    } else if (typeOfPiece === 'customSole') {
      loadedTexture.wrapS = THREE.RepeatWrapping;
      loadedTexture.wrapT = THREE.MirroredRepeatWrapping;
      loadedTexture.offset.x = 0.5;
      loadedTexture.repeat.x = 0.5;
      loadedTexture.repeat.y = 0.5;
      newCustomSoles = newCustomSoles.map((el, elIndex) => {
        const deltaIndex =
          (activePieces.length ?? 0) + (customSteles.length ?? 0) + (customPlatings.length ?? 0);
        if (index >= deltaIndex && index < deltaIndex + (customSoles.length ?? 0)) {
          if (hasAdvancedMode && index - deltaIndex !== elIndex) return el;
          const extrudeGeometry = new THREE.ExtrudeGeometry(el.shape, {
            curveSegments: 12,
            steps: 2,
            depth: el.shapeDetails.depth,
            bevelEnabled: false,
          });
          if (granit.material !== 'INOX') {
            checkMonumentSizeWithGranit(
              // @ts-ignore
              extrudeGeometry.attributes.position.array,
              String(granit.maxLength),
              setError,
            );
          }

          return {
            ...el,
            granit,
            texture: loadedTexture,
          };
        }
        return el;
      });
    }
  }

  setActivePieces(newActivePieces);
  setCustomPlatings(newCustomPlating);
  setCustomSteles(newCustomStele);
  setCustomSoles(newCustomSoles);
};

export const selectedTexture = (
  pieceIndex: number,
  activePieces: any[],
  customSteles: any[],
  customPlatings: any[],
  customSoles: any[],
) => {
  const typeOfPiece = getIndexType(
    pieceIndex,
    activePieces,
    customSteles,
    customPlatings,
    customSoles,
  );
  const localIndex = getLocalIndex(
    pieceIndex,
    activePieces,
    customSteles,
    customPlatings,
    customSoles,
  );

  if (typeOfPiece === 'activePieces') {
    return activePieces[localIndex]?.granit;
  }
  if (typeOfPiece === 'customStele') {
    return customSteles[localIndex]?.granit;
  }
  if (typeOfPiece === 'customPlating') {
    return customPlatings[localIndex]?.granit;
  }
  if (typeOfPiece === 'customSole') {
    return customSoles[localIndex]?.granit;
  }
};

export const selectPieceWithGranitId = (
  granitId: string,
  activePieces: any[],
  customSteles: any[],
  customPlatings: any[],
  customSoles: any[],
  setPieceIndex: (index: number) => void,
) => {
  for (const [elIndex, el] of activePieces.entries()) {
    if (el.granit?.id === granitId) {
      const globalIndex = getGlobalIndex(
        elIndex,
        'activePieces',
        activePieces,
        customSteles,
        customPlatings,
        customSoles,
      );
      setPieceIndex(globalIndex);
      return;
    }
  }
  for (const [elIndex, el] of customSteles.entries()) {
    if (el.granit?.id === granitId) {
      const globalIndex = getGlobalIndex(
        elIndex,
        'customStele',
        activePieces,
        customSteles,
        customPlatings,
        customSoles,
      );
      setPieceIndex(globalIndex);
      return;
    }
  }
  for (const [elIndex, el] of customPlatings.entries()) {
    if (el.granit?.id === granitId) {
      const globalIndex = getGlobalIndex(
        elIndex,
        'customPlating',
        activePieces,
        customSteles,
        customPlatings,
        customSoles,
      );
      setPieceIndex(globalIndex);
      return;
    }
  }
  for (const [elIndex, el] of customSoles.entries()) {
    if (el.granit?.id === granitId) {
      const globalIndex = getGlobalIndex(
        elIndex,
        'customSole',
        activePieces,
        customSteles,
        customPlatings,
        customSoles,
      );
      setPieceIndex(globalIndex);
      return;
    }
  }
};
